import { faFlask, faHome } from "@fortawesome/free-solid-svg-icons";
import { percent } from "csx";
import { Vertical } from "gls";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { classes } from "typestyle";
import { useContextSelector } from "use-context-selector";
import { PermissionType } from "@/pages/Configuration/AdminConfiguration/Users/types/cognitoTypes";
import { DashboardsContext } from "@/shared/context/Dashboards";
import { usePermissions } from "shared/hooks/usePermissions";
import { Dashboards } from "../Dashboards";
import { defaultSearchParams } from "../Sidebar";
import { SidebarItem } from "../SidebarItem";
import { SidebarItemGroup } from "../SidebarItemGroup";
import { SIDEBAR_ROUTES, SidebarRoute } from "../routes";
import { useIsStageOrDev } from "@/shared/hooks/useIsStageOrDev";
import { isEmergencySession } from "@/exported/shared/utils/emergencySession";

export const SideBarMainMenu: FC<{
  isCollapsed: boolean;
}> = ({ isCollapsed }) => {
  const { t } = useTranslation();
  const isStageOrDev = useIsStageOrDev();
  const isElectron = !!window?.electronAPI;
  const isEmergency = isEmergencySession();
  const { hasPermission } = usePermissions();
  const dashboards = useContextSelector(
    DashboardsContext,
    (context) => context.dashboards,
  );

  const isVisible = (route: SidebarRoute) =>
    hasPermission(route.permission) && (!route.electronOnly || isElectron);
  const visibleTrack = SIDEBAR_ROUTES.track.filter(isVisible);
  const visibleExplore = SIDEBAR_ROUTES.explore.filter(isVisible);
  const visibleAct = SIDEBAR_ROUTES.act.filter(isVisible);
  const hasDashboards =
    hasPermission(PermissionType.Dashboard) && !!dashboards?.length;

  const renderItem = (route: SidebarRoute) => (
    <SidebarItem
      key={route.path}
      isCollapsed={isCollapsed}
      icon={route.icon}
      svgIcon={route.svgIcon}
      text={t(route.text)}
      collapsedText={t(route.collapsedText)}
      navigateTo={route.path}
      navigateSearch={route.withSearch ? defaultSearchParams : undefined}
      permissionName={route.permission}
      data-testid={route.testId}
    />
  );

  return (
    <Vertical
      sizing="stretch"
      width={percent(100)}
      className={classes(
        "overflow-y-auto sm:pt-0 md:pt-20",
        !isCollapsed && "pr-2",
      )}
    >
      {isStageOrDev && !isEmergency && (
        <SidebarItemGroup label="Shell" isCollapsed={isCollapsed}>
          <SidebarItem
            isCollapsed={isCollapsed}
            icon={faHome}
            text={t("Home")}
            collapsedText={t("Home")}
            navigateTo={"/welcome"}
            data-testid="shell-home-link"
          />
          <SidebarItem
            isCollapsed={isCollapsed}
            icon={faFlask}
            text="Test MFE"
            collapsedText="Test"
            navigateTo={"/test-mfe"}
            data-testid="shell-test-mfe-link"
          />
        </SidebarItemGroup>
      )}
      {visibleTrack.length > 0 && (
        <SidebarItemGroup label="Track" isCollapsed={isCollapsed}>
          {visibleTrack.map(renderItem)}
        </SidebarItemGroup>
      )}
      {(hasDashboards || visibleExplore.length > 0) && (
        <SidebarItemGroup label="Explore" isCollapsed={isCollapsed}>
          <Dashboards isCollapsed={isCollapsed} />
          {visibleExplore.map(renderItem)}
        </SidebarItemGroup>
      )}
      {visibleAct.length > 0 && (
        <SidebarItemGroup label="Act" isCollapsed={isCollapsed}>
          {visibleAct.map(renderItem)}
        </SidebarItemGroup>
      )}
    </Vertical>
  );
};
